import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { GROW_METHODS } from "@/lib/sizes";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "all", label: "All" },
  { key: "flower", label: "Flower" },
  { key: "blunt", label: "Blunts" },
  { key: "concentrate", label: "Concentrates" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const active = params.get("c") || "all";
  const collectionParam = params.get("collection") || "";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [terpQuery, setTerpQuery] = useState("");
  const [terpMin, setTerpMin] = useState("");
  const [grow, setGrow] = useState("");
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth?redirect=/shop");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    const url = collectionParam || active === "all" ? "/products" : `/products?category=${active}`;
    api.get(url).then(({ data }) => setProducts(data)).finally(() => setLoading(false));
  }, [active, user, collectionParam]);

  const setTab = (k) => setParams(k === "all" ? {} : { c: k });

  const q = terpQuery.trim().toLowerCase();
  const minPct = parseFloat(terpMin);
  const hasMin = !isNaN(minPct);
  const filtering = q || hasMin || grow;
  const visible = products.filter((p) => {
    const collectionOk = !collectionParam || (p.collection || "").toLowerCase() === collectionParam.toLowerCase();
    if (!collectionOk) return false;
    const growOk = !grow || p.grow_method === grow;
    if (!growOk) return false;
    if (!q && !hasMin) return true;
    return (p.terpenes || []).some((t) => {
      const nameOk = !q || (t.name || "").toLowerCase().includes(q);
      const pctOk = !hasMin || (parseFloat(t.percentage) || 0) >= minPct;
      return nameOk && pctOk;
    });
  });

  const clearFilters = () => {
    setTerpQuery("");
    setTerpMin("");
    setGrow("");
  };

  if (authLoading || !user) return null;

  return (
    <section className="mx-auto max-w-7xl px-6 py-16">
      <div className="mb-8 flex flex-col items-start justify-between gap-6 border-b border-black/10 pb-6 sm:flex-row sm:items-end">
        <div>
          <p className="overline mb-2">{collectionParam ? "Collection" : "The Collection"}</p>
          <h1 className="text-4xl font-black text-[#2b2520]">
            {collectionParam ? `${collectionParam}` : "The Good Life"}
          </h1>
        </div>
        {collectionParam ? (
          <button
            data-testid="clear-collection"
            onClick={() => setParams({})}
            className="border border-black/15 px-5 py-2 text-xs font-semibold uppercase tracking-widest text-neutral-600 transition-colors duration-300 hover:border-[#c99a63] hover:text-[#a06d34]"
          >
            ← View Full Shop
          </button>
        ) : (
          <div className="flex flex-wrap gap-2">
            {TABS.map((t) => (
              <button
                key={t.key}
                data-testid={`tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-5 py-2 text-xs font-semibold uppercase tracking-widest transition-colors duration-300 ${
                  active === t.key
                    ? "bg-[#c99a63] text-[#2b2520]"
                    : "border border-black/15 text-neutral-600 hover:border-[#c99a63] hover:text-[#a06d34]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mb-12 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" strokeWidth={1.5} />
          <input
            data-testid="terpene-search"
            value={terpQuery}
            onChange={(e) => setTerpQuery(e.target.value)}
            placeholder="Search by terpene (e.g. Limonene, Myrcene)"
            className="w-full border border-black/15 bg-white py-2.5 pl-9 pr-3 text-sm text-[#2b2520] outline-none focus:border-[#c99a63]"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-neutral-500">Min %</span>
          <input
            data-testid="terpene-min"
            type="number"
            step="0.1"
            min="0"
            value={terpMin}
            onChange={(e) => setTerpMin(e.target.value)}
            placeholder="e.g. 2"
            className="w-24 border border-black/15 bg-white py-2.5 px-3 text-sm text-[#2b2520] outline-none focus:border-[#c99a63]"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-neutral-500">Grown</span>
          <select
            data-testid="grow-filter"
            value={grow}
            onChange={(e) => setGrow(e.target.value)}
            className="border border-black/15 bg-white py-2.5 px-3 text-sm text-[#2b2520] outline-none focus:border-[#c99a63]"
          >
            <option value="">All grows</option>
            {GROW_METHODS.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
        </div>
        {filtering && (
          <button
            data-testid="clear-terpene-search"
            onClick={clearFilters}
            className="text-xs uppercase tracking-widest text-neutral-500 hover:text-[#a06d34]"
          >
            Clear
          </button>
        )}
      </div>

      {loading ? (
        <p className="py-20 text-center text-neutral-500">Loading…</p>
      ) : visible.length === 0 ? (
        <p className="py-20 text-center text-neutral-500" data-testid="no-products">
          {collectionParam
            ? `No products in the ${collectionParam} collection yet.`
            : filtering
            ? "No products match your current filters."
            : "No products available yet."}
        </p>
      ) : (
        <div
          data-testid="product-grid"
          className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          {visible.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      )}
    </section>
  );
}
